import { Controller } from "@hotwired/stimulus"

const RETRY_DELAYS = [0, 250, 1000, 3000, 10000]
const LEDGER_VERSION = 2
const GA4_PROBE_TIMEOUT = 1000

export default class extends Controller {
    static values = {
        partyId: Number,
        partyToken: String,
        googleId: Number,
        googleLabel: String,
        googleAnalyticsId: String,
        cohort: String,           // "NewHost" | "GuestToHost" | "ReturningHost"
        hasStd: Boolean
    }

    connect() {
        this.connection = {}
        this.ga4Ready = false
        this.ga4Probe = null
        this.timers = []
        this.ledger = this.loadLedger()
        this.libraryReadyHandler = () => this.attemptDelivery()
        this.consentChangedHandler = () => this.attemptDelivery()
        this.visibilityHandler = () => {
            if (document.visibilityState === "visible") this.attemptDelivery()
        }

        window.addEventListener("echoage:tracking-library-ready", this.libraryReadyHandler)
        window.addEventListener("echoage:tracking-consent-changed", this.consentChangedHandler)
        document.addEventListener("visibilitychange", this.visibilityHandler)

        RETRY_DELAYS.forEach((delay) => {
            this.timers.push(window.setTimeout(() => this.attemptDelivery(), delay))
        })
    }

    disconnect() {
        this.connection = null
        this.timers?.forEach((timer) => window.clearTimeout(timer))
        window.clearTimeout(this.ga4Probe?.timer)
        window.clearTimeout(this.ga4ReadyTimer)
        window.clearTimeout(this.markRetryTimer)
        window.removeEventListener("echoage:tracking-library-ready", this.libraryReadyHandler)
        window.removeEventListener("echoage:tracking-consent-changed", this.consentChangedHandler)
        document.removeEventListener("visibilitychange", this.visibilityHandler)
    }

    attemptDelivery() {
        if (!this.connection || this.ledger.serverMarked) return

        this.prepareGoogleAnalytics()

        this.deliver("googleAds", "google", () => this.fireGoogleConversion())
        this.deliver("googleCohort", "analytics", () => this.fireGoogleEvent(this.cohortValue, "cohort"))

        if (this.hasStdValue) {
            this.deliver("googleSaveDate", "analytics", () => this.fireGoogleEvent("SaveDateToHost", "save-date"))
        }

        this.deliver("facebookCohort", "facebook", () => this.fireFacebookEvent("trackCustom", this.cohortValue, "cohort"))
        this.deliver("facebookRegistration", "facebook", () => this.fireFacebookEvent("track", "CompleteRegistration", "registration"))
        this.deliver("tiktokRegistration", "tiktok", () => this.fireTikTokEvent())

        this.persistLedger()
        if (this.allRequiredDeliveriesCompleted()) this.markFired()
    }

    deliver(key, platform, callback) {
        if (this.ledger.deliveries[key]) return
        if (this.consentState(platform) !== "granted") return

        try {
            if (callback()) this.ledger.deliveries[key] = true
        } catch (error) {
            // A failure on one platform must not suppress the others.
            console.warn(`[conversion-tags] ${key} dispatch failed:`, error)
        }
    }

    consentState(platform) {
        // ECHOage currently does not override platform consent. If a consent
        // manager is introduced, it can expose per-platform or `marketing`
        // state here and dispatch `echoage:tracking-consent-changed` on updates.
        const consent = window.echoageTrackingConsent
        if (!consent) return "granted"

        let value
        try {
            value = typeof consent === "function"
                ? consent(platform)
                : (consent[platform] ?? consent.marketing)
        } catch (error) {
            console.warn("[conversion-tags] could not read tracking consent:", error)
            return "pending"
        }

        if (value === true || value === "granted") return "granted"
        if (value === false || value === "denied") return "denied"
        return "pending"
    }

    fireGoogleConversion() {
        const transactionId = this.conversionId()

        if (this.libraryReady("googleTag") && typeof window.gtag === "function") {
            window.gtag("event", "conversion", {
                send_to: `${this.googleIdValue}/${this.googleLabelValue}`,
                value: 0,
                transaction_id: transactionId
            })
            return true
        }

        if (this.libraryReady("googleLegacy") && typeof window.google_trackConversion === "function") {
            window.google_trackConversion({
                google_conversion_id: this.googleIdValue,
                google_conversion_label: this.googleLabelValue,
                google_conversion_value: 0,
                google_conversion_order_id: transactionId,
                google_remarketing_only: false
            })
            return true
        }

        return false
    }

    fireGoogleEvent(label, suffix) {
        if (!this.ga4Ready || typeof window.gtag !== "function") return false

        const category = "PartyBooking"
        const action = "KidsPartyBooking"
        const eventId = this.eventId(`google-${suffix}`)
        const fields = {
            send_to: this.googleAnalyticsIdValue,
            event_category: category,
            event_label: label,
            event_id: eventId,
            transaction_id: this.conversionId()
        }

        // Dispatch to the existing GA4 destination only. A UA call cannot fulfil
        // this delivery, and Ads/GTM script load alone says nothing about routing.
        window.gtag("event", action, fields)
        return true // SDK dispatch bookkeeping only, never a platform receipt.
    }

    prepareGoogleAnalytics() {
        if (this.ga4Ready || this.ga4Probe || this.consentState("analytics") !== "granted") return
        if (this.ledger.deliveries.googleCohort && (!this.hasStdValue || this.ledger.deliveries.googleSaveDate)) return
        if (!/^G-[A-Z0-9]+$/.test(this.googleAnalyticsIdValue || "")) return
        if (!(this.libraryReady("googleTag") || this.libraryReady("googleTagManager"))) return
        if (typeof window.gtag !== "function") return

        const connection = this.connection
        const probe = {}
        this.ga4Probe = probe
        // Only the read-only readiness probe is retryable. Never retry an event
        // just because its callback/receipt is absent. A delayed probe may still
        // complete after its timeout; the connection/ledger guards prevent repeats.
        probe.timer = window.setTimeout(() => {
            if (this.ga4Probe === probe) this.ga4Probe = null
        }, GA4_PROBE_TIMEOUT)

        try {
            window.gtag("get", this.googleAnalyticsIdValue, "client_id", (value) => {
                if (this.connection !== connection || this.ga4Ready) return
                window.clearTimeout(probe.timer)
                if (this.ga4Probe === probe) this.ga4Probe = null
                if (this.consentState("analytics") !== "granted") return
                if (typeof value !== "string" || value.length === 0) return

                // Do not retain, log or transmit the identifier. This only shows
                // that the configured destination handled a local get command;
                // it is not proof of collection, generated events or attribution.
                this.ga4Ready = true
                this.ga4ReadyTimer = window.setTimeout(() => {
                    if (this.connection === connection) this.attemptDelivery()
                }, 0)
            })
        } catch (_error) {
            window.clearTimeout(probe.timer)
            if (this.ga4Probe === probe) this.ga4Probe = null
            console.warn("[conversion-tags] GA4 destination readiness probe failed")
        }
    }

    fireFacebookEvent(command, eventName, suffix) {
        if (!this.libraryReady("facebook") || typeof window.fbq !== "function") return false

        window.fbq(command, eventName, {}, { eventID: this.eventId(`facebook-${suffix}`) })
        return true
    }

    fireTikTokEvent() {
        if (!this.libraryReady("tiktok") || typeof window.ttq?.track !== "function") return false

        window.ttq.track(
            "CompleteRegistration",
            {
                description: "Party booking confirmed",
                value: "0",
                currency: "CAD"
            },
            { event_id: this.eventId("tiktok-registration") }
        )
        return true
    }

    libraryReady(name) {
        return window.echoageTrackingLibraries?.[name] === true
    }

    allRequiredDeliveriesCompleted() {
        const required = [
            "googleAds",
            "googleCohort",
            "facebookCohort",
            "facebookRegistration",
            "tiktokRegistration"
        ]
        if (this.hasStdValue) required.push("googleSaveDate")
        return required.every((key) => this.ledger.deliveries[key] === true)
    }

    async markFired() {
        if (this.marking || this.ledger.serverMarked) return
        this.marking = true

        try {
            const token = document.querySelector('meta[name="csrf-token"]')?.content
            const response = await fetch(`/dashboard/${this.partyTokenValue}/mark_conversion_tags_fired`, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
                credentials: "same-origin",
                body: JSON.stringify({ conversion_id: this.conversionId() })
            })

            if (!response.ok) throw new Error(`bookkeeping returned ${response.status}`)
            this.ledger.serverMarked = true
            this.persistLedger()
        } catch (error) {
            console.warn("[conversion-tags] could not save dispatch bookkeeping:", error)
            window.clearTimeout(this.markRetryTimer)
            this.markRetryTimer = window.setTimeout(() => this.markFired(), 5000)
        } finally {
            this.marking = false
        }
    }

    conversionId() {
        return `echoage-party-${this.partyIdValue}`
    }

    eventId(suffix) {
        return `${this.conversionId()}-${suffix}`
    }

    storageKey() {
        return `echoage:conversion-delivery:v${LEDGER_VERSION}:${this.partyIdValue}`
    }

    loadLedger() {
        const emptyLedger = { version: LEDGER_VERSION, deliveries: {}, serverMarked: false }

        try {
            const parsed = JSON.parse(window.localStorage.getItem(this.storageKey()) || "null")
            if (parsed?.version !== LEDGER_VERSION || typeof parsed.deliveries !== "object") return emptyLedger
            return {
                version: LEDGER_VERSION,
                deliveries: parsed.deliveries,
                serverMarked: parsed.serverMarked === true
            }
        } catch (_error) {
            return emptyLedger
        }
    }

    persistLedger() {
        // Do not create tracking storage before consent has allowed at least
        // one dispatch. An existing ledger can still be read to prevent a
        // duplicate if consent is later withdrawn.
        if (!this.ledger.serverMarked && Object.keys(this.ledger.deliveries).length === 0) return

        try {
            window.localStorage.setItem(this.storageKey(), JSON.stringify(this.ledger))
        } catch (_error) {
            // Storage may be unavailable; the server flag remains the final guard.
        }
    }
}
